import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep, map, upperFirst } from 'lodash-es';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import {
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  Company,
  ISitemateTaskDefinitionWarning,
  SitemateTaskTypes,
  Team,
} from '@site-mate/dashpivot-shared-library';

import { SegmentService } from 'app/segment/segment.service';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { LinkCreateStatus } from 'app/shared/model/sitemate/link-status.enum';
import { TaskCreationMessage } from 'app/shared/model/sitemate/task-creation-message.model';
import { TaskCreationMode } from 'app/shared/model/sitemate/task-creation-mode.enum';
import { ISitemateTaskDefinitionWeb } from 'app/shared/model/task-definition.model';
import { TemplateWeb } from 'app/shared/model/template.model';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { TeamService } from 'app/shared/service/team.service';

@Component({
  selector: 'cc-template-links-modal',
  templateUrl: './template-links-modal.component.html',
  styleUrls: ['./template-links-modal.component.scss'],
})
export class TemplateLinksModalComponent implements OnInit {
  readonly linkStatus = { ...LinkCreateStatus };
  readonly taskType = { ...SitemateTaskTypes };
  templates: TemplateWeb[];
  company: Company;
  team: Team;
  taskDefinition?: ISitemateTaskDefinitionWeb;
  taskCreationMode: TaskCreationMode;
  warnings: ISitemateTaskDefinitionWarning[] = [];
  type: SitemateTaskTypes;
  status: LinkCreateStatus = LinkCreateStatus.Initial;
  isLoading = true;
  context: string;

  private readonly destroy$ = new Subject<void>();
  @Output() onLinkCreated = new EventEmitter<ISitemateTaskDefinitionWeb>();

  constructor(
    private readonly modal: NgbActiveModal,
    private readonly routeParamsService: RouteParamsService,
    private readonly teamService: TeamService,
    private readonly segmentService: SegmentService,
  ) {}

  ngOnInit(): void {
    this.context = this.getContext();
    this.team = this.teamService.getCurrentTeam();
    this.loadTemplates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNewTaskCreated({ taskDefinition, warnings, mode }: TaskCreationMessage): void {
    this.taskDefinition = taskDefinition;
    this.taskCreationMode = mode;
    this.updateToCompleteStatus(warnings);
    this.onLinkCreated.emit(this.taskDefinition);

    this.trackContextualEvents(mode);
  }

  private trackContextualEvents(taskCreationMode: TaskCreationMode): void {
    const eventsForModes = {
      [TaskCreationMode.Created]: EventTypes.SitemateLinkCreated,
      [TaskCreationMode.Updated]: EventTypes.SitemateLinkEdited,
      [TaskCreationMode.Restored]: EventTypes.SitemateLinkRestored,
    };
    const segmentEventName = eventsForModes[taskCreationMode];

    void EventNotifierService.notify(
      new DashpivotEvent(segmentEventName, {
        Context: this.context,
        Hierarchy: upperFirst(AppHierarchy.Team),
      }),
      this.segmentService,
    );
  }

  onLinkCopied(): void {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.SitemateLinkCopied, {
        Context: this.context,
        Hierarchy: upperFirst(AppHierarchy.Team),
      }),
      this.segmentService,
    );
  }

  onQrCodePosterDownload(): void {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.SitemateLinkPosterDownloaded, {
        Context: this.getContext(),
        Hierarchy: upperFirst(AppHierarchy.Team),
      }),
      this.segmentService,
    );
  }

  onCloseModal(): void {
    this.modal.dismiss();
  }

  onMoveToCompleted(): void {
    this.status = LinkCreateStatus.Completed;
  }

  private getContext(): string {
    return this.type === SitemateTaskTypes.FormSingle ? 'Single' : 'Ongoing';
  }

  private loadTemplates(): void {
    combineLatest([
      this.routeParamsService.get('templates').pipe(takeUntil(this.destroy$)),
      this.routeParamsService.get('companyFromTeam').pipe(takeUntil(this.destroy$)),
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([templates, company]) => !!templates && !!company),
      )
      .subscribe(([templates, company]) => {
        // cloneDeep avoids touching the originally provided entries, which is undesirable due to side-effects
        this.templates = this.sortTemplates(cloneDeep(templates));
        this.company = cloneDeep(company);
        this.isLoading = false;
        this.markSelectedTemplates();
      });
  }

  private updateToCompleteStatus(warnings: ISitemateTaskDefinitionWarning[]) {
    if (warnings.length) {
      this.warnings = warnings;
      this.status = LinkCreateStatus.CompletedWithWarnings;
    } else {
      this.status = LinkCreateStatus.Completed;
    }
  }

  private markSelectedTemplates(): void {
    const selectedTemplateIds = map(this.taskDefinition?.templateMetadata, '_id');
    this.templates.forEach((template) => {
      template.selected =
        [LinkCreateStatus.Edit, LinkCreateStatus.Unarchive].includes(this.status) &&
        selectedTemplateIds.includes(template.id);
    });
  }

  private sortTemplates(templates: TemplateWeb[]): TemplateWeb[] {
    return templates.sort(({ name }, { name: secondName }) => name.localeCompare(secondName));
  }
}
